import { useState, useEffect, useRef } from 'react';
import { 
  Plus, Search, Filter, Edit2, Trash2, Eye, EyeOff, Star, 
  Loader2, Image as ImageIcon, X, ChevronLeft, ChevronRight,
  Package, Upload, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

export default function AdminShopProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category_id: '',
    stock_status: '',
    is_featured: '',
    is_active: ''
  });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_hindi: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    stock: 0,
    sku: '',
    tags: '',
    deity: '',
    occasion: '',
    is_featured: false,
    is_active: true,
    specifications: {}
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const deityOptions = ['Shiv', 'Vishnu', 'Brahma', 'Ganesh', 'Hanuman', 'Durga', 'Lakshmi', 'Saraswati', 'Krishna', 'Ram', 'Other'];
  const occasionOptions = ['Daily Pooja', 'Diwali', 'Navratri', 'Ganesh Chaturthi', 'Janmashtami', 'Shivratri', 'Holi', 'Raksha Bandhan', 'Wedding', 'Griha Pravesh', 'Satyanarayan Pooja', 'Other'];

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [page, search, filters]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.stock_status) params.append('stock_status', filters.stock_status);
      if (filters.is_featured !== '') params.append('is_featured', filters.is_featured);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      
      const response = await axios.get(`${API}/products?${params}`, getAuthHeaders());
      setProducts(response.data.products);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, getAuthHeaders());
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '', name_hindi: '', description: '', price: '', original_price: '',
      category_id: categories[0]?.id || '', stock: 0, sku: '', tags: '',
      deity: '', occasion: '', is_featured: false, is_active: true, specifications: {}
    });
    setShowModal(true);
  };

  const openEditModal = async (productId) => {
    try {
      const response = await axios.get(`${API}/products/${productId}`, getAuthHeaders());
      const product = response.data;
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        name_hindi: product.name_hindi || '',
        description: product.description || '',
        price: product.price || '',
        original_price: product.original_price || '',
        category_id: product.category_id || '',
        stock: product.stock || 0,
        sku: product.sku || '',
        tags: (product.tags || []).join(', '),
        deity: product.deity || '',
        occasion: product.occasion || '',
        is_featured: product.is_featured || false,
        is_active: product.is_active !== false,
        specifications: product.specifications || {}
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load product');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock),
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, getAuthHeaders());
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, payload, getAuthHeaders());
        toast.success('Product created successfully');
      }
      
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/products/${product.id}`, getAuthHeaders());
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const toggleFeatured = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, 
        { is_featured: !product.is_featured }, 
        getAuthHeaders()
      );
      toast.success(product.is_featured ? 'Removed from featured' : 'Marked as featured');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleActive = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, 
        { is_active: !product.is_active }, 
        getAuthHeaders()
      );
      toast.success(product.is_active ? 'Product hidden' : 'Product visible');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingProduct) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/products/${editingProduct.id}/images`,
        formData,
        { 
          ...getAuthHeaders(),
          headers: { 
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data' 
          }
        }
      );
      
      toast.success('Image uploaded');
      // Refresh product data
      const updated = await axios.get(`${API}/products/${editingProduct.id}`, getAuthHeaders());
      setEditingProduct(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!editingProduct) return;
    
    try {
      await axios.delete(
        `${API}/products/${editingProduct.id}/images?image_url=${encodeURIComponent(imageUrl)}`,
        getAuthHeaders()
      );
      toast.success('Image deleted');
      // Refresh product data
      const updated = await axios.get(`${API}/products/${editingProduct.id}`, getAuthHeaders());
      setEditingProduct(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const getStockBadge = (stock) => {
    if (stock === 0) return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Out of Stock</span>;
    if (stock <= 10) return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">Low: {stock}</span>;
    return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">In Stock: {stock}</span>;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Products</h1>
          <p className="text-slate-500 text-sm mt-1">Manage shop products</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          data-testid="add-product-btn"
        >
          <Plus className="w-5 h-5" />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
              />
            </div>
          </div>
          
          <select
            value={filters.category_id}
            onChange={(e) => { setFilters({...filters, category_id: e.target.value}); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          <select
            value={filters.stock_status}
            onChange={(e) => { setFilters({...filters, stock_status: e.target.value}); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="">All Stock</option>
            <option value="in_stock">In Stock</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
          
          <select
            value={filters.is_active}
            onChange={(e) => { setFilters({...filters, is_active: e.target.value}); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Hidden</option>
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Product</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Price</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Stock</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon className="w-5 h-5 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{product.name}</p>
                          <p className="text-xs text-slate-500">SKU: {product.sku || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{product.category_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-slate-900">₹{product.price}</span>
                        {product.original_price && (
                          <span className="text-xs text-slate-400 line-through ml-1">₹{product.original_price}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">{getStockBadge(product.stock)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {product.is_featured && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Featured</span>
                        )}
                        {!product.is_active && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">Hidden</span>
                        )}
                        {product.is_active && !product.is_featured && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleFeatured(product)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            product.is_featured ? 'bg-amber-100 text-amber-600' : 'hover:bg-slate-100 text-slate-400'
                          }`}
                          title={product.is_featured ? 'Remove from featured' : 'Mark as featured'}
                        >
                          <Star className="w-4 h-4" fill={product.is_featured ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          onClick={() => toggleActive(product)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            product.is_active ? 'hover:bg-slate-100 text-slate-400' : 'bg-red-100 text-red-600'
                          }`}
                          title={product.is_active ? 'Hide product' : 'Show product'}
                        >
                          {product.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => openEditModal(product.id)}
                          className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 hover:bg-slate-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hindi Name</label>
                  <input
                    type="text"
                    value={formData.name_hindi}
                    onChange={(e) => setFormData({...formData, name_hindi: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  required
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (₹) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    min="1"
                    step="0.01"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Original Price (₹)</label>
                  <input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  />
                  {formData.price && formData.original_price && formData.original_price > formData.price && (
                    <p className="text-xs text-green-600 mt-1">
                      Discount: {Math.round((1 - formData.price / formData.original_price) * 100)}% off
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    min="0"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    required
                  />
                </div>
              </div>

              {/* Category & SKU */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    placeholder="Auto-generated if empty"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="e.g., diwali, brass, pooja"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
              </div>

              {/* Deity & Occasion */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Deity Association</label>
                  <select
                    value={formData.deity}
                    onChange={(e) => setFormData({...formData, deity: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  >
                    <option value="">Select Deity</option>
                    {deityOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Pooja Occasion</label>
                  <select
                    value={formData.occasion}
                    onChange={(e) => setFormData({...formData, occasion: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
                  >
                    <option value="">Select Occasion</option>
                    {occasionOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              {/* Toggles */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="w-4 h-4 text-amber-500 rounded focus:ring-amber-200"
                  />
                  <span className="text-sm text-slate-700">Featured Product</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 text-amber-500 rounded focus:ring-amber-200"
                  />
                  <span className="text-sm text-slate-700">Active (Visible)</span>
                </label>
              </div>

              {/* Images - Only for editing */}
              {editingProduct && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Product Images</label>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {editingProduct.images?.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(img)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-amber-400 hover:text-amber-500 transition-colors"
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span className="text-xs mt-1">Upload</span>
                        </>
                      )}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  {(!editingProduct.images || editingProduct.images.length === 0) && (
                    <p className="text-xs text-orange-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Product must have at least one image
                    </p>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
